
import _findIndex from 'lodash/findIndex';

import _keys from 'lodash/keys';

import moment from 'moment';

import {
  defineComponent,
  defineAsyncComponent,
  reactive,
  ref,
  computed,
  watch,
  onMounted,
} from 'vue';

import { useRoute } from 'vue-router';

import type {
  GenericObject,
  Projects,
  Countries,
  Platforms,
  Browsers,
  Statistics,
} from '@/types';

import {
  TRAFFIC_SOURCES,
  BIDDER_FEED,
  BIDDER_ADVERTISER,
  BIDDERS,
  TRAFFIC_CATEGORIES,
  TRAFFIC_TYPES,
  PRESELECTED_PROJECTS_IDS,
} from '@/libs/consts';

import useUser from '@/composable/useUser';

import useEntriesNext from '@/composable/useEntries@next';

import useTimezonePreference from '@/composable/useTimezonePreference';

import useStatisticsLink from '@/composable/useStatisticsLink';

import useSubStatisticsComponent from '@/composable/useSubStatisticsComponent';

export default defineComponent({
  components: {
    // Компоненты колонок
    EfficiencyColumn: defineAsyncComponent(() => import('@/components/Statistics/Staff/Columns/EfficiencyColumn.vue')),
    RequestsColumn: defineAsyncComponent(() => import('@/components/Statistics/Staff/Columns/RequestsColumn.vue')),
    ViewsColumn: defineAsyncComponent(() => import('@/components/Statistics/Staff/Columns/ViewsColumn.vue')),
    FeedsRequestsColumn: defineAsyncComponent(() => import('@/components/Statistics/Staff/Columns/FeedsRequestsColumn.vue')),
    ClicksColumn: defineAsyncComponent(() => import('@/components/Statistics/Staff/Columns/ClicksColumn.vue')),
    ImpressionsColumn: defineAsyncComponent(() => import('@/components/Statistics/Staff/Columns/ImpressionsColumn.vue')),
    CTRColumn: defineAsyncComponent(() => import('@/components/Statistics/Staff/Columns/CTRColumn.vue')),
    // Компоненты статистики
    Actions: defineAsyncComponent(() => import('@/components/Statistics/Staff/Actions.vue')),
    StatisticsByHours: defineAsyncComponent(() => import('@/components/Statistics/Staff/StatisticsByHours.vue')),
    StatisticsByPublishers: defineAsyncComponent(() => import('@/components/Statistics/Staff/StatisticsByPublishers.vue')),
    StatisticsByEndpoints: defineAsyncComponent(() => import('@/components/Statistics/Staff/StatisticsByEndpoints.vue')),
    StatisticsBySubids: defineAsyncComponent(() => import('@/components/Statistics/Staff/StatisticsBySubids.vue')),
    StatisticsByReferrers: defineAsyncComponent(() => import('@/components/Statistics/Staff/StatisticsByReferrers.vue')),
    StatisticsByCountries: defineAsyncComponent(() => import('@/components/Statistics/Staff/StatisticsByCountries.vue')),
    StatisticsByPlatforms: defineAsyncComponent(() => import('@/components/Statistics/Staff/StatisticsByPlatforms.vue')),
    StatisticsByBrowsers: defineAsyncComponent(() => import('@/components/Statistics/Staff/StatisticsByBrowsers.vue')),
    StatisticsByProjects: defineAsyncComponent(() => import('@/components/Statistics/Staff/StatisticsByProjects.vue')),
    StatisticsByAdvertisers: defineAsyncComponent(() => import('@/components/Statistics/Staff/StatisticsByAdvertisers.vue')),
    StatisticsByCampaigns: defineAsyncComponent(() => import('@/components/Statistics/Staff/StatisticsByCampaigns.vue')),
    StatisticsByCreatives: defineAsyncComponent(() => import('@/components/Statistics/Staff/StatisticsByCreatives.vue')),
    StatisticsByFeeds: defineAsyncComponent(() => import('@/components/Statistics/Staff/StatisticsByFeeds.vue')),
    StatisticsByFilteredClicks: defineAsyncComponent(() => import('@/components/Statistics/Staff/StatisticsByFilteredClicks.vue')),
    StatisticsByFilteredImpressions: defineAsyncComponent(() => import('@/components/Statistics/Staff/StatisticsByFilteredImpressions.vue')),
  },
  setup() {
    const route = useRoute();

    const { activeUser: user } = useUser();

    const {
      entries: projects,
      entriesLoading: projectsLoading,
      fetchEntries: fetchProjects,
    } = useEntriesNext<Projects>('/api/project/getProjects');

    const {
      entries: countries,
      entriesLoading: countriesLoading,
      fetchEntries: fetchCountries,
    } = useEntriesNext<Countries>('/api/country/getCountries');

    const {
      entries: platforms,
      entriesLoading: platformsLoading,
      fetchEntries: fetchPlatforms,
    } = useEntriesNext<Platforms>('/api/platform/getPlatforms');

    const {
      entries: browsers,
      entriesLoading: browsersLoading,
      fetchEntries: fetchBrowsers,
    } = useEntriesNext<Browsers>('/api/browser/getBrowsers');

    const {
      entries: statistics,
      entriesLoading: statisticsLoading,
      entriesFetched: statisticsFetched,
      fetchEntries: fetchStatistics,
    } = useEntriesNext<Statistics>('/api/statistics/staff/getStatisticsByDays');

    const { savedTimezone, watchTimezone } = useTimezonePreference();

    const {
      encodeStatisticsFilters,
      decodeStatisticsFilters,
    } = useStatisticsLink();

    const {
      subStatisticsComponent,
      setSubStatisticsComponent,
      resetSubStatisticComponent,
    } = useSubStatisticsComponent();

    const defaultStatisticsFilters = {
      project_id: user.isSuperAdmin() ? PRESELECTED_PROJECTS_IDS : [],
      date_from: moment().subtract(4, 'days').format('YYYY-MM-DD'),
      date_to: moment().format('YYYY-MM-DD'),
      timezone: savedTimezone,
      traffic_source: null,
      traffic_type: null,
      traffic_category: null,
      publisher_id: [],
      endpoint_id: [],
      subid_uuid: null,
      bidder: null,
      feed_id: [],
      advertiser_id: [],
      campaign_id: [],
      campaign_folder_id: [],
      country_id: null,
      platform_id: null,
      browser_id: null,
      show_requests: false,
      show_feeds_requests: false,
      show_unique_clicks: false,
      show_feeds: true,
      show_campaigns: true,
    } as GenericObject;

    const queryStatisticsFilters = {} as GenericObject;

    if (route.query.filters) {
      Object.assign(queryStatisticsFilters, decodeStatisticsFilters(route.query.filters as string));
    }

    const statisticsFilters = reactive<GenericObject>({
      ...defaultStatisticsFilters,
      ...queryStatisticsFilters,
    });

    const statisticsFiltersSnapshot = computed(() => ({ ...statisticsFilters }));

    watchTimezone(() => statisticsFilters.timezone);

    const usersParams = computed(() => ({
      include: 'project',
      'filter[project_id]': user.isSuperAdmin() ? statisticsFilters.project_id.join(',') : undefined,
    }));

    const endpointsParams = computed(() => ({
      include: 'project,user',
      'filter[project_id]': user.isSuperAdmin() ? statisticsFilters.project_id.join(',') : undefined,
      'filter[user_id]': statisticsFilters.publisher_id.join(','),
    }));

    const feedsParams = computed(() => ({
      include: 'project',
      'filter[project_id]': user.isSuperAdmin() ? statisticsFilters.project_id.join(',') : undefined,
    }));

    const campaignsParams = computed(() => ({
      include: 'project,user',
      'filter[project_id]': user.isSuperAdmin() ? statisticsFilters.project_id.join(',') : undefined,
      'filter[user_id]': statisticsFilters.advertiser_id.join(','),
    }));

    watch(
      statisticsFilters,
      (value: GenericObject) => {
        const encodedStatisticsFilters = encodeStatisticsFilters(value);

        // eslint-disable-next-line no-restricted-globals
        history.pushState(
          {},
          '',
          `${route.path}?${new URLSearchParams({ filters: encodedStatisticsFilters }).toString()}`,
        );
      },
    );

    const statisticsFiltersKeyCounter = ref<number>(0);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const statisticsFilterIsEmpty = (value: any): boolean => value === null || value === '' || (Array.isArray(value) && !value.length);

    const resetStatisticsFilters = (): void => {
      Object.assign(statisticsFilters, defaultStatisticsFilters);

      statisticsFiltersKeyCounter.value += 1;
    };

    // 🎨

    const showExtraData = ref<boolean>(false);

    const getStatisticByDayRowClass = (row: GenericObject): string => (row.date === subStatisticsComponent.filters.date ? 'statistics-table-row-active' : '');

    const iconSize = 10;

    //

    const fetchStatisticsWrapper = async (): Promise<void> => {
      fetchStatistics(statisticsFilters);

      resetSubStatisticComponent();

      statisticsFiltersKeyCounter.value += 1;
    };

    const onPublisherChange = (): void => {
      statisticsFilters.endpoint_id = [];
    };

    const onAdvertiserChange = (): void => {
      statisticsFilters.campaign_id = [];
    };

    const onProjectChange = (): void => {
      statisticsFilters.publisher_id = [];

      onPublisherChange();

      statisticsFilters.subid_uuid = null;

      statisticsFilters.advertiser_id = [];

      onAdvertiserChange();

      statisticsFilters.feed_id = [];
    };

    const onBidderChange = (): void => {
      if (statisticsFilters.bidder === BIDDER_FEED) {
        statisticsFilters.advertiser_id = [];

        statisticsFilters.show_feeds = true;

        statisticsFilters.show_campaigns = false;
      }

      if (statisticsFilters.bidder === BIDDER_ADVERTISER) {
        statisticsFilters.feed_id = [];

        statisticsFilters.show_feeds = false;

        statisticsFilters.show_campaigns = true;
      }

      if (statisticsFilters.bidder === null) {
        statisticsFilters.show_feeds = false;

        statisticsFilters.show_campaigns = false;
      }
    };

    // Скрытие колонки с количеством запросов

    // Список не поддерживаемых фильтров нужно синхронизировать с RequestsColumn в platform-api
    const requestsColumnUnsupportedFilters = [
      'feed_id',
      'advertiser_id',
      'campaign_id',
      'campaign_folder_id',
    ];

    // 😨
    const hideRequestsColumn = computed((): boolean => _findIndex(requestsColumnUnsupportedFilters, (key: keyof GenericObject) => !statisticsFilterIsEmpty(statisticsFilters[key])) !== -1 || !statisticsFilters.show_requests);

    // Скрытие колонки с количеством запросов на фиды

    // Список не поддерживаемых фильтров нужно синхронизировать с FeedsRequestsColumn в platform-api
    const feedsRequestsColumnUnsupportedFilters = [
      'advertiser_id',
      'campaign_id',
      'campaign_folder_id',
    ];

    // 😨
    const hideFeedsRequestsColumn = computed((): boolean => _findIndex(feedsRequestsColumnUnsupportedFilters, (key: keyof GenericObject) => !statisticsFilterIsEmpty(statisticsFilters[key])) !== -1 || !statisticsFilters.show_feeds_requests);

    // Скрытие колонки с фактической выплатой фидов

    // Список поддерживаемых фильтров нужно синхронизировать с getStatisticsByDays и getStatisticsByFeeds в platform-api
    const feedsPayoutColumnSupportedFilters = [
      'project_id',
      'date_from',
      'date_to',
      'date',
      'timezone',
      'publisher_id',
      'endpoint_id',
      'bidder',
      'feed_id',
      'traffic_type',
      'traffic_category',
      'show_requests',
      'show_feeds',
      'show_feeds_requests',
      'show_campaigns',
      'show_unique_clicks',
    ];

    const feedsPayoutColumnUnsupportedFilters = _keys(statisticsFilters).filter((key: string) => !feedsPayoutColumnSupportedFilters.includes(key));

    // 😨
    const hideFeedsPayoutColumn = computed((): boolean => _findIndex(feedsPayoutColumnUnsupportedFilters, (key: keyof GenericObject) => {
      // Костыли с доп. проверкой подъехали
      if (key === 'bidder') {
        return !statisticsFilterIsEmpty(statisticsFilters[key]) && statisticsFilters.bidder !== BIDDER_FEED;
      }

      return !statisticsFilterIsEmpty(statisticsFilters[key]);
    }) !== -1);

    onMounted(() => Promise.all([
      user.isSuperAdmin() ? fetchProjects() : Promise.resolve(),
      fetchCountries(),
      fetchPlatforms(),
      fetchBrowsers(),
    ]));

    return {
      TRAFFIC_SOURCES,
      BIDDER_FEED,
      BIDDER_ADVERTISER,
      BIDDERS,
      TRAFFIC_CATEGORIES,
      TRAFFIC_TYPES,
      user,
      projects,
      projectsLoading,
      countries,
      countriesLoading,
      platforms,
      platformsLoading,
      browsers,
      browsersLoading,
      statistics,
      statisticsLoading,
      statisticsFetched,
      subStatisticsComponent,
      setSubStatisticsComponent,
      statisticsFilters,
      statisticsFiltersSnapshot,
      usersParams,
      endpointsParams,
      feedsParams,
      campaignsParams,
      statisticsFiltersKeyCounter,
      resetStatisticsFilters,
      fetchStatisticsWrapper,
      onProjectChange,
      onPublisherChange,
      onAdvertiserChange,
      onBidderChange,
      hideRequestsColumn,
      hideFeedsRequestsColumn,
      hideFeedsPayoutColumn,
      showExtraData,
      getStatisticByDayRowClass,
      iconSize,
    };
  },
});
